import { components } from '@doltech/dto';
import { DynamicPageProps } from '@doltech/routeDefinition';
import { useUserAuthentication } from '@doltech/ui/lib/figma/Auth/hocs/withUserAuthentication';
import { useDolPremiumStore } from '@doltech/ui/lib/hooks/dolPremium/withDolPremium';
import { useDeviceDetect } from '@doltech/ui/lib/hooks/useDeviceQuery.hook';
import { TestModeEnum, TestTypeEnum } from '@doltech/utils/lib/constants';
import { observer } from 'mobx-react-lite';
import { onAction } from 'mobx-state-tree';
import { useCreateStore, useProvider } from 'mobx-store-provider';
import { useRouter } from 'next/router';
import * as React from 'react';

import { TestResult } from '../student-test-result/layouts/TestResult';
import { TestResultMobile } from '../student-test-result/mobile/TestResultMobile';
import {
  getInitializeTestResultStore,
  useTestResultStore,
} from '../student-test-result/model/test-result.hook';
import { TestResultStore } from '../student-test-result/model/test-result.store';
import { TestResultTablet } from '../student-test-result/tablet/TestResultTablet';
import { TestResultDesktopTheme } from '../student-test-result/theme/TestResultDesktopTheme';
import { TestResultMobileTheme } from '../student-test-result/theme/TestResultMobileTheme';
import { useGetAuthInfo } from '@doltech/core/lib/auth/withGlobalUserAuthInfo';

const ACTION_REQUIRE_LOGIN = ['preventUserAction'];

interface ViewExplanationProps extends DynamicPageProps {
  data: components['schemas']['UserFullTestDTO'] | components['schemas']['UserPracticeTestDTO'];
}

const ViewExplanationBase = observer((props: Omit<ViewExplanationProps, 'data'>) => {
  const { internalPageLink } = props;
  const { isMobile, isTablet } = useDeviceDetect();
  const store = useTestResultStore();
  const { isLoggedIn } = useGetAuthInfo();
  const { forceLoginToProceedAction } = useUserAuthentication();
  const premiumStore = useDolPremiumStore();
  const router = useRouter();

  const defaultBack = React.useMemo(() => {
    if (TestTypeEnum.FULL_TEST === store.testType) {
      return internalPageLink.staticPageLink.LANDING_FULL_TEST.url;
    }
    return internalPageLink.staticPageLink[`LANDING_${store.skill}_PRACTICE_TEST`].url;
  }, [store.testType, store.skill, internalPageLink.staticPageLink]);

  React.useEffect(() => {
    store.registerPremiumStore(premiumStore);
    store.setIsLogged(isLoggedIn);
  }, [isLoggedIn, premiumStore, store]);

  const onQuit = React.useCallback(() => {
    router.replace(defaultBack);
  }, [defaultBack, router]);

  React.useEffect(() => {
    if (store) {
      const { questionNo = 1 } = router.query;
      store.goToQuestion(questionNo, false);
    }
  }, [router.query, store]);

  React.useEffect(() => {
    const disposer = onAction(store, async (call) => {
      if (call.name === 'close') {
        onQuit();
        return;
      }
      if (ACTION_REQUIRE_LOGIN.includes(call.name)) {
        // if (isLoggedIn && premiumStore.isExpired && store?.skill === "READING") {
        //   premiumStore.setModalVisible(true);
        // }

        if (!isLoggedIn) {
          forceLoginToProceedAction(() => {}, {
            title:
              '<span>Để <span class="red">xem giải thích</span> bạn cần phải đăng ký tài khoản!</span>',
            defaultBack,
          });
        }
      }
    });
    return () => {
      disposer();
    };
  }, [defaultBack, forceLoginToProceedAction, isLoggedIn, onQuit, store]);

  const renderContent = React.useCallback(() => {
    if (isMobile) {
      return (
        <TestResultMobileTheme>
          <TestResultMobile onQuit={onQuit} />
        </TestResultMobileTheme>
      );
    }
    if (isTablet) {
      return (
        <TestResultMobileTheme>
          <TestResultTablet onQuit={onQuit} />
        </TestResultMobileTheme>
      );
    }
    return (
      <TestResultDesktopTheme>
        <TestResult onQuit={onQuit} />
      </TestResultDesktopTheme>
    );
  }, [isMobile, isTablet, onQuit]);

  return renderContent();
});

export const ViewExplanation = (props: ViewExplanationProps) => {
  const { data, ...rest } = props;

  const testResultCreateStore = useCreateStore(TestResultStore, {
    ...getInitializeTestResultStore(data),
    testMode: TestModeEnum.EXPLANATION,
  });
  const TestResultStoreProvider = useProvider(TestResultStore);

  return (
    <TestResultStoreProvider value={testResultCreateStore}>
      <ViewExplanationBase {...rest} />
    </TestResultStoreProvider>
  );
};
